import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faLock,
  faArrowRight,
  faSpinner,
  faUser,
} from "@fortawesome/free-solid-svg-icons";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);

    try {
      const result = await login(email, password);
      // Role-based redirect
      if (result.role === "admin" || result.role === "super_admin") {
        navigate("/dashboard");
      } else if (result.role === "supplier") {
        navigate("/supplier-dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="auth-page"
    >
      <div className="auth-container">
        <div className="auth-header">
          <FontAwesomeIcon icon={faUser} className="auth-icon" />
          <h1>Welcome Back</h1>
          <p>Login to your Kenali Enterprise account</p>
        </div>

        {error && (
          <div className="auth-error">
            <FontAwesomeIcon icon={faLock} /> {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="auth-form">
          <div className="form-group">
            <label>
              <FontAwesomeIcon icon={faEnvelope} /> Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label>
              <FontAwesomeIcon icon={faLock} /> Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
            <div className="input-hint">
              <Link to="/forgot-password">Forgot password?</Link>
            </div>
          </div>

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Logging in...
              </>
            ) : (
              <>
                Login <FontAwesomeIcon icon={faArrowRight} />
              </>
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Don't have an account? <Link to="/register">Register here</Link>
          </p>
        </div>

        <div className="auth-demo">
          <p>
            Demo: admin@wrightagro.sl / Admin123! <br />
            Demo Farmer: farmer@kenali.sl / Farmer123!
          </p>
        </div>

        <style>
          {`
          .auth-page {
            min-height: calc(100vh - 120px);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1.5rem;
            background: #f8f8f8;
          }
          @media (min-width: 768px) {
            .auth-page {
              padding: 2rem;
              min-height: calc(100vh - 140px);
            }
          }

          .auth-container {
            background: #ffffff;
            border-radius: 12px;
            padding: 1.5rem;
            max-width: 450px;
            width: 100%;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          }
          @media (min-width: 768px) {
            .auth-container {
              padding: 2.5rem;
              border-radius: 16px;
            }
          }

          .auth-header {
            text-align: center;
            margin-bottom: 1.5rem;
          }
          @media (min-width: 768px) {
            .auth-header {
              margin-bottom: 2rem;
            }
          }

          .auth-icon {
            font-size: 2.5rem;
            color: #d9a321;
            margin-bottom: 0.5rem;
          }

          .auth-header h1 {
            color: #006b2e;
            font-size: 1.5rem;
            margin-bottom: 0.25rem;
          }
          @media (min-width: 768px) {
            .auth-header h1 {
              font-size: 1.75rem;
            }
          }

          .auth-header p {
            color: #5a5a5a;
            font-size: 0.875rem;
          }

          .auth-error {
            background: #fee2e2;
            color: #dc2626;
            padding: 0.625rem 0.875rem;
            border-radius: 8px;
            margin-bottom: 0.75rem;
            font-size: 0.813rem;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
          }

          .auth-form .form-group {
            margin-bottom: 1rem;
          }
          @media (min-width: 768px) {
            .auth-form .form-group {
              margin-bottom: 1.25rem;
            }
          }

          .auth-form .form-group label {
            display: block;
            margin-bottom: 0.25rem;
            font-weight: 500;
            font-size: 0.813rem;
            color: #1f1f1f;
          }
          @media (min-width: 768px) {
            .auth-form .form-group label {
              margin-bottom: 0.375rem;
              font-size: 0.875rem;
            }
          }

          .auth-form .form-group label svg {
            margin-right: 0.5rem;
            color: #d9a321;
          }

          .auth-form .form-group input {
            width: 100%;
            padding: 0.625rem 0.75rem;
            border: 1px solid #e5e5e5;
            border-radius: 8px;
            font-size: 0.875rem;
            transition: border-color 0.3s ease;
            background: #ffffff;
            color: #1f1f1f;
          }
          @media (min-width: 768px) {
            .auth-form .form-group input {
              padding: 0.75rem;
              font-size: 1rem;
            }
          }

          .auth-form .form-group input:focus {
            outline: none;
            border-color: #d9a321;
            box-shadow: 0 0 0 3px rgba(217, 163, 33, 0.15);
          }

          .auth-form .form-group .input-hint {
            font-size: 0.7rem;
            color: #5a5a5a;
            margin-top: 0.25rem;
            text-align: right;
          }
          .auth-form .form-group .input-hint a {
            color: #d9a321;
            text-decoration: none;
          }
          .auth-form .form-group .input-hint a:hover {
            text-decoration: underline;
          }

          .auth-submit {
            width: 100%;
            padding: 0.625rem;
            background: #d9a321;
            color: #ffffff;
            border: none;
            border-radius: 8px;
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            min-height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
          }
          @media (min-width: 768px) {
            .auth-submit {
              padding: 0.75rem;
              font-size: 1rem;
            }
          }

          .auth-submit:hover:not(:disabled) {
            background: #b8860b;
            transform: translateY(-2px);
          }

          .auth-submit:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }

          .auth-footer {
            text-align: center;
            margin-top: 1.25rem;
            padding-top: 1.25rem;
            border-top: 1px solid #e5e5e5;
          }
          @media (min-width: 768px) {
            .auth-footer {
              margin-top: 1.5rem;
              padding-top: 1.5rem;
            }
          }

          .auth-footer a {
            color: #d9a321;
            text-decoration: none;
            font-weight: 500;
          }
          .auth-footer a:hover {
            text-decoration: underline;
          }

          .auth-demo {
            margin-top: 1rem;
            padding: 0.5rem;
            background: #f8f8f8;
            border-radius: 8px;
            font-size: 0.75rem;
            text-align: center;
            color: #5a5a5a;
          }
        `}
        </style>
      </div>
    </motion.div>
  );
};

export default Login;
